import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { t } from 'i18next'
import { EventSourcePolyfill } from 'event-source-polyfill'
import { useCommonState } from '@/core/context/useContext'
import { useConfirm, usePopup } from '@/core/context/useWindow'
import useLogout from '@/core/hooks/useLogout'
import useRefreshMoney from '@/core/hooks/useRefreshMoney'
import useToast from '@/core/hooks/useToast'
import { update, dataInitalState } from '@/core/store/data/actions'
import { clearUser } from '@/core/store/user/actions'
import { formatMoney, getBaseCurrency } from '@/constant/currency'
import Login from '@/components/popup/Login'
import logoGoldM from '@/assets/image/logo_gold_m.png'
import iconsSprite from '@/assets/icons.svg'
import * as S from './styled'

export default function MHeader() {
  const { isLogin } = useCommonState()
  const { openPopup } = usePopup()
  const { openConfirm } = useConfirm()
  const { logout } = useLogout()
  const dispatch = useDispatch()
  const infoData = useSelector((state) => state.Data)
  const authUser = useSelector((state) => state.User) || {}
  // 새로고침 아이콘 — 페이지를 다시 불러오지 않고 아이콘을 돌리며 머니만 갱신
  const { spinning: moneySpinning, refresh: refreshMoney } = useRefreshMoney()
  const [reconnectCount, setReconnectCount] = useState(0)
  const MAX_RECONNECT_ATTEMPTS = 5
  const { Toast, openToast } = useToast()
  const [toastMsg, setToastMsg] = useState('')

  useEffect(() => {
    if (!authUser?.id) return

    try {
      const sse = new EventSourcePolyfill(
        `${process.env.REACT_APP_BASE_URL}/v1/SSE/header-alarm`,
        { withCredentials: true, heartbeatTimeout: 45000 }
      )

      sse.addEventListener('update', (e) => {
        setReconnectCount(0)
        if (e?.data) {
          try {
            const userData = JSON.parse(e.data)
            if (userData?.bet_type === 'win') {
              const timer = userData.game_type === 'slot' ? 500 : 3200
              setTimeout(() => { dispatch(update(userData)) }, timer)
            } else {
              dispatch(update(userData))
            }
          } catch (error) {
            console.error('SSE Header Alarm update 이벤트 파싱 오류:', error)
          }
        }
      })

      sse.addEventListener('userLogout', (e) => {
        if (e?.data) {
          try {
            dispatch(dataInitalState())
            dispatch(clearUser())
            window.location.href = '/'
          } catch (error) {
            console.error('SSE Header Alarm userLogout 이벤트 파싱 오류:', error)
          }
        }
      })

      // 베팅 한도(최소/최대) 초과 거절 알림
      sse.addEventListener('betReject', (e) => {
        if (e?.data) {
          try {
            const { kind } = JSON.parse(e.data)
            setToastMsg(t('bet_reject.' + kind))
            openToast()
          } catch (error) {
            console.error('SSE Header Alarm betReject 이벤트 파싱 오류:', error)
          }
        }
      })

      sse.onerror = () => {
        console.error('SSE Header Alarm 연결 오류')
        sse.close()
        if (reconnectCount < MAX_RECONNECT_ATTEMPTS) {
          setTimeout(() => setReconnectCount(prev => prev + 1), 3000)
        }
      }

      return () => { sse.close() }
    } catch (error) {
      console.error('Error Header Alarm SSE:', error)
    }
  }, [authUser?.id, reconnectCount, dispatch])

  const openLoginPopup = (e) => {
    e.preventDefault()
    openPopup({ grandLogin: true, title: t('modalTitle.login'), content: <Login isMobile /> })
  }

  const handleLogout = () => {
    openConfirm({ message: t('alert.islogout'), Func: () => logout() })
  }

  // gold8.bet 모바일: 금색 띠, 왼쪽 로고 · 오른쪽 (로그인 전) 빨간 테두리 ログイン 버튼 /
  //   (로그인 후) 빨간 원 사용자 아이콘 + 아이디·잔액 두 줄 + 새로고침 + 로그아웃
  return (
    <S.GoldMHeader>
      <Toast toastText={toastMsg} />
      <S.GoldMLogo href="/m">
        <img src={logoGoldM} alt="" />
      </S.GoldMLogo>
      {isLogin ? (
        <S.GoldMAccount>
          <S.GoldMUserIcon aria-hidden="true">
            <svg viewBox="0 0 32 32"><use href={`${iconsSprite}#ico-user2`} /></svg>
          </S.GoldMUserIcon>
          <S.GoldMInfo>
            <S.GoldMId>{authUser?.id}</S.GoldMId>
            <S.GoldMMoney>
              {getBaseCurrency()} {formatMoney(infoData?.money ?? 0)}
              <S.GoldMIconBtn type="button" aria-label="refresh" aria-busy={moneySpinning} $spinning={moneySpinning} onClick={refreshMoney}>
                <svg viewBox="0 0 16 16"><use href={`${iconsSprite}#ico-reset`} /></svg>
              </S.GoldMIconBtn>
            </S.GoldMMoney>
          </S.GoldMInfo>
          <S.GoldMIconBtn type="button" aria-label="logout" $big onClick={handleLogout}>
            <svg viewBox="0 0 22 22"><use href={`${iconsSprite}#ico-logout`} /></svg>
          </S.GoldMIconBtn>
        </S.GoldMAccount>
      ) : (
        <S.GoldMLoginBtn type="button" onClick={openLoginPopup}>{t('header.login')}</S.GoldMLoginBtn>
      )}
    </S.GoldMHeader>
  )
}
